import "./about.scss";

// images
import { images } from "../../../constants/images";

const About = () => {
  return (
    <div className="about">
      <div className="content">
        <div className="appTitle">
          <span>What is HAIQU?</span>
        </div>
        <span className="sectionTitle">Introduction</span>
        <div className="sectionContent">
          <p>
            If you are introducing Artificial Intelligence (AI) into an aviation
            context, especially an AI designed to support human end users, then
            HAIQU is for you. HAIQU has reviewed EASA’s regulatory requirements
            for Human-AI interactive systems in aviation, as well as
            requirements arising from the EU Act on AI, and Human Factors
            guidance from platforms such as SESAR, EASA regulation CS25/1302,
            and the Human Factors Compass.
          </p>
          <p>
            The result is a questionnaire divided into eight Human Factors
            areas, from Human Centred Design to Organisational Readiness. Each
            individual question highlights a specific design element considered
            to be a critical determinant of safe Human-AI performance. The aim
            of the questionnaire is therefore to help developers realise a
            highly usable and safe Human-AI system, one that end users can use
            effectively and will want to use. The approach has been tested on
            several Human-AI projects, leading to design improvements accepted
            by the product development team, and a robust set of satisfied
            requirements that pave the way for certification.
          </p>
        </div>
        <span className="sectionTitle">How it works</span>
        <div className="sectionContent">
          <p>
            HAIQU operates in two modes, guided and non-guided. In the default
            mode, HAIQU guides the user through the question set. Often, not all
            areas or questions need to be answered, depending on the AI system’s
            maturity (e.g. from early research project, to ready-to-deploy), its
            EASA classification (from 1A to 3B), and its intended capabilities
            (e.g. from textual interaction to speech and gesture recognition).
            In the non-guided approach, you can select any Human Factors area or
            question you see as relevant to your project.  
          </p>
          <p>
            As you use HAIQU, a dashboard updates how far along in the
            evaluation process you are, as well as showing progress in each of
            the eight areas, or the subset of areas associated with your
            project. All your responses, including justifications, evidence or
            arguments made to satisfy each requirement, are recorded. Any
            further actions that you identify, or further information needs, are
            summarised on the dashboard.
          </p>
          <p>
            HAIQU is comprehensive, and it may not be possible to complete all
            relevant questions in one sitting. Some questions may be
            non-applicable, some will be quick to answer, and others may take
            more time and require deeper consideration. HAIQU works best when
            used collectively by core members of the product development team –
            including the product owner, intended end-users, data scientists or
            AI specialists, and Human Factors or Safety experts. Usually, one
            person cannot answer all the questions, as they cover a range of
            areas or expertise. <br />
            In the early stages of a project, a number of questions will have to
            be returned to at a later stage in the development life cycle, but
            it is better to highlight them early rather than wait until the
            design is too advanced to be changed. Once the system is near
            deployment, all questions can be answered, and the organisation will
            have a robust basis upon which to deploy and operate the Human-AI
            system.
          </p>
        </div>
        <span className="sectionTitle">What's under the hood?</span>
        <span
          className="subSectionTitle"
          style={{
            marginTop: "12px",
          }}
        >
          The Eight Human Factors Areas{" "}
        </span>
        <div className="sectionContent">
          <p>
            The figure below shows the eight Human Factors Areas and the issues
            they relate to. These are the ‘classic’ Human Factors application
            areas, in use in cockpit and air traffic centre and tower design for
            many years, updated to account for new AI architectures and
            capabilities. Five of the areas (Roles, Sense-Making,
            Communications, Competencies, and Organisational Readiness) break
            down into sub-categories. For example, Sense-Making includes shared
            situation awareness, trustworthiness, explainability and abnormal
            event handling. Each area or sub-area contains a set of questions
            (minimum 5, maximum 15) relating to more detailed requirements. The
            questions are all answerable depending on the anticipated AI
            capability and the maturity of design. Many are very
            straightforward, requiring no or minimum Human Factors expertise,
            but some will benefit from Human Factors expertise.  Example answers
            to each question are given, to help the development team.
          </p>
          <img
            style={{ marginTop: "48px" }}
            src={images.about_img1}
            alt="Eight human factors areas"
          />
        </div>
        <span className="subSectionTitle">
          EASA Human-AI Teaming Categories, and the intended AI Capability
        </span>
        <div className="sectionContent">
          <p>
            The six EASA categories begin with 1A (Human Augmentation), which is
            effectively advanced automation giving the end users better
            information, and helping them ‘think wider, deeper and faster’. This
            level of AI barely needs special considerations for Human-AI
            interaction, but since it is being integrated into a safety critical
            environment, HAIQU can still be of use as a Human Factors assurance
            system. Level 1B (cognitive Assistant) is decision support, helping
            the end user optimise decision-making via more powerful, accurate
            and rapid information analysis. In both categories the human is in
            charge. For both categories, Sense-Making is the key Human Factors
            area.
          </p>
          <p>
            In Categories 2A (Cooperation) and 2B (Collaboration), the AI
            carries out tasks. The main difference is that in 2A there is a
            hierarchy – the human is in charge, and delegates to the AI, and
            checks / accepts its output. In 2B, there is no effective hierarchy,
            since the AI can make decisions and act without seeking human
            approval (though the human can possibly still intervene). From a
            Human Factors perspective, Category 2B offers the most challenge, as
            this is not a usual situation for an aviation end-user. Roles &
            Responsibilities, Sense-Making, Communication and Error & Failure
            Management are especially key for 2A systems, whereas for 2B all
            areas are critical.
          </p>
          <p>
            In category 3A, the human is ‘managing by exception’, only
            intervening when required, whether such need is detected by the
            human, or the AI asks for help. The key issue is whether the human,
            who is relatively ‘out-of-the-loop’, can gain situation awareness
            quickly enough to take over when required, and still has the
            necessary skill-sets. For 3B, Sense-Making, Error & Failure
            Management, and Training are the key Human Factors areas. For
            Category 3B, this is autonomous AI, with no human involvement.
            Nevertheless, the Human Factors Organisational Readiness may be of
            use, in particular Staffing and User Acceptance.
          </p>
        </div>
        <span className="subSectionTitle">
          AI System Maturity & Technology Readiness Levels (TRLs)
        </span>
        <div className="sectionContent">
          <img
            style={{ marginBottom: "16px", marginTop: "16px" }}
            src={images.about_img2}
            alt="AI System Maturity & Technology Readiness Levels (TRLs)"
          />
          <p>
            In order to chart the maturity of a concept design, aviation often
            refers to NASA’s Technology Readiness Levels (TRLs). These proceed
            from 1 to 9. Typically, TRLs 1-2 are concerned with early concept
            exploration. For Human-AI System design, at this TRL usually the
            most important considerations focus on Roles and Responsibilities,
            namely who (or what) will be in charge when the AI is operating.
            Decisions about sense-making such as how shared situation awareness
            will be established, and primary human-Ai interaction modes, e.g.
            ‘conventional’ (keyboard, mouse, touchscreen, etc.) or more advanced
            (speech, gesture recognition) can be made, as well as whether the AI
            will be used by a single person or a team. It is at this stage that
            key decisions are made concerning the adoption of a Human Centred
            Design approach, including the use of end users in informing the
            early ‘foundational’ concept.
          </p>
          <p>
            TRLs 2-4 add a lot more detail, fleshing out the concept’s
            architecture, and gaining a picture of what the AI might look and
            feel like to interact with, via early prototypes and walk-throughs
            of human-AI interaction scenarios. The area of sense-making is
            crucial here, and communication and teamworking aspects will become
            clearer.
          </p>
          <p>
            At TRLs 5-6 models and prototypes will be developed iteratively
            until a full-scope demonstration is completed and tested in a
            realistic simulation with operational end users. This period of
            design and development will see many issues ‘nailed down’ and
            solidified into the design and operational concept (CONOPS), having
            been validated via robust testing with human end users. Risk studies
            will focus on errors and failures seen, as well as those that could
            conceivably occur once the system is in operation.
          </p>
          <p>
            TRLs 7-9 prepare the concept for deployment in real world settings.
            Now is the time to consider in earnest the ramifications of entering
            a human-AI system into an operational organisation, with
            requirements for competencies and training of end users, as well as
            the often ‘thornier’ socio-technical systems issues such as
            staffing, user acceptance, ethics and wellbeing. These latter issues
            often determine whether the system will be accepted and used to its
            full extent. <br />
            After TRL 9 the system is in operational use. However, since most AI
            systems are learning systems, monitoring – particularly in the first
            six to twelve months – will be a critical determinant of sustainable
            system performance. <br />
            The relevance of the Human Factors areas to the different TRL are
            highlighted in the main HAIQU figure, to help the user adapt their
            evaluation approach. As illustrated in the figure, the application
            of Human Factors areas to TRLs are not always clear cut, so some
            degree of judgement must be used. <br />
            For many projects, in early TRLs entire areas can be deemed ‘TBD’
            (to be decided later) or even N/A (not applicable – for now) and
            returned to in later design stages. For example, many research
            projects will not consider Human Factors areas 7 & 8, although it is
            worth considering impacts on staffing early, since this can be a
            ‘deal-breaker’ for any project.
          </p>
        </div>
      </div>
    </div>
  );
};

export default About;
